import { useCallback } from "react"

import type { MetricCatalogItem } from "@/metrics/configure"
import { toast } from "@coval/ui"

import { logger } from "@/lib/logger"
import type {
  ConditionalMonitoringRuleResource as TransportConversationMetricRule,
  UpdateWorkspaceSettingsRequest,
} from "@/types_api/backend/transport/update_workspace_settings_request"
import type { ConversationMetricRule } from "@/types/api/ConversationMetricRule"

import { normalizeConversationMetricIds } from "../utils/conversationMetricIdentity"
import { validateAndMigrateRule } from "../utils/conversationMetricsConfigurationRules"

const log = logger("ConversationMetricsConfigureController")

const toError = (error: unknown, fallbackMessage: string): Error =>
  error instanceof Error ? error : new Error(fallbackMessage)

const toTransportRule = (
  rule: ConversationMetricRule,
): TransportConversationMetricRule => ({
  id: rule.id,
  type: rule.type,
  metrics: rule.metrics && rule.metrics.length > 0 ? rule.metrics : null,
  metadata_key: rule.metadataKey,
  metadata_type: rule.metadataType,
  match_type: rule.matchType,
  metadata_value: rule.metadataValue,
  metadata_conditions: rule.metadataConditions?.map((condition) => ({
    metadata_key: condition.metadataKey,
    metadata_type: condition.metadataType,
    match_type: condition.matchType,
    metadata_value: condition.metadataValue,
  })),
})

interface UseConversationMetricsConfigureControllerParams {
  metrics: MetricCatalogItem[]
  tempSelectedMetrics: string[]
  tempRules: ConversationMetricRule[]
  saveScope: "all" | "default" | "conditional"
  isSaving: boolean
  beginSavingChanges: () => void
  finishSavingChanges: () => void
  markAllChangesSaved: () => void
  updateSettings: (
    variables: UpdateWorkspaceSettingsRequest,
  ) => Promise<unknown>
  invalidateSettings: () => void
}

export const useConversationMetricsConfigureController = ({
  metrics,
  tempSelectedMetrics,
  tempRules,
  saveScope,
  isSaving,
  beginSavingChanges,
  finishSavingChanges,
  markAllChangesSaved,
  updateSettings,
  invalidateSettings,
}: UseConversationMetricsConfigureControllerParams) => {
  const handleSaveChanges = useCallback(async () => {
    if (isSaving) {
      throw new Error("Save already in progress")
    }

    beginSavingChanges()
    try {
      const settingsUpdate: UpdateWorkspaceSettingsRequest = {}

      if (saveScope !== "conditional") {
        settingsUpdate.default_metrics = normalizeConversationMetricIds(
          tempSelectedMetrics,
          metrics,
          true,
        )
      }

      if (saveScope !== "default") {
        settingsUpdate.conditional_metrics = tempRules
          .map(validateAndMigrateRule)
          .map((rule) => ({
            ...rule,
            metrics: normalizeConversationMetricIds(
              rule.metrics ?? [],
              metrics,
              true,
            ),
          }))
          .map(toTransportRule)
      }

      await updateSettings(settingsUpdate)

      markAllChangesSaved()
      toast.success({
        title: "Success",
        description: "Conversation metrics configuration saved successfully",
      })
      invalidateSettings()
    } catch (error) {
      const saveError = toError(
        error,
        "Failed to update workspace metric settings",
      )
      log.error("Save failed", saveError)
      toast.danger({
        title: "Error",
        description: "Failed to save changes. Please try again.",
      })
      throw saveError
    } finally {
      finishSavingChanges()
    }
  }, [
    isSaving,
    metrics,
    saveScope,
    tempSelectedMetrics,
    tempRules,
    beginSavingChanges,
    finishSavingChanges,
    markAllChangesSaved,
    updateSettings,
    invalidateSettings,
  ])

  return { handleSaveChanges }
}
